import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeftIcon, SaveIcon, Loader2Icon } from 'lucide-react'
import { supabase } from '../lib/supabase'
import {
  MobilePage,
} from '../components/mobile'

interface ServiceCategory {
  id: string
  name: string
}

interface CombinationRule {
  id?: string
  category_id: string
  combination_group: string
  is_standalone: boolean
}

export function CombinationRules() {
  const navigate = useNavigate()
  const [categories, setCategories] = useState<ServiceCategory[]>([])
  const [rules, setRules] = useState<Record<string, CombinationRule>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [existingGroups, setExistingGroups] = useState<string[]>([])

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      const [{ data: cats }, { data: rulesData }] = await Promise.all([
        supabase
          .from('service_categories')
          .select('id, name')
          .eq('is_active', true)
          .order('name', { ascending: true }),
        supabase
          .from('service_category_combination_rules')
          .select('id, category_id, combination_group, is_standalone'),
      ])

      setCategories(cats || [])

      const rulesMap: Record<string, CombinationRule> = {}
      const groups = new Set<string>()
      for (const r of rulesData || []) {
        rulesMap[r.category_id] = r
        if (r.combination_group) groups.add(r.combination_group)
      }
      setRules(rulesMap)
      setExistingGroups(Array.from(groups).sort())
    } catch (e) {
      console.error('Failed to load combination rules:', e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const updateRule = (catId: string, field: 'combination_group' | 'is_standalone', value: any) => {
    setRules((prev) => {
      const existing = prev[catId] || {
        category_id: catId,
        combination_group: '',
        is_standalone: false,
      }
      const updated = { ...existing, [field]: value }

      if (field === 'is_standalone' && value === true) {
        updated.combination_group = ''
      }

      return { ...prev, [catId]: updated }
    })
  }

  const clearRule = (catId: string) => {
    setRules((prev) => {
      const next = { ...prev }
      delete next[catId]
      return next
    })
  }

  const handleSave = async () => {
    setSaving(true)
    setMessage(null)
    try {
      const { error: delError } = await supabase
        .from('service_category_combination_rules')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000')
      if (delError) throw delError

      const rows = Object.values(rules).filter(
        (r) => r.combination_group.trim() !== '' || r.is_standalone
      )

      if (rows.length > 0) {
        const inserts = rows.map((r) => ({
          category_id: r.category_id,
          combination_group: r.is_standalone
            ? `standalone_${r.category_id}`
            : r.combination_group.trim(),
          is_standalone: r.is_standalone,
        }))
        const { error: insError } = await supabase
          .from('service_category_combination_rules')
          .insert(inserts)
        if (insError) throw insError
      }

      setMessage({ type: 'success', text: 'Combination rules saved.' })
      await fetchData()
    } catch (e: any) {
      console.error('Failed to save combination rules:', e)
      setMessage({ type: 'error', text: 'Failed to save: ' + (e?.message || 'Unknown error') })
    } finally {
      setSaving(false)
      setTimeout(() => setMessage(null), 5000)
    }
  }

  const getCategoryStatus = (catId: string): string => {
    const rule = rules[catId]
    if (!rule) return 'Not configured (standalone by default)'
    if (rule.is_standalone) return 'Standalone'
    if (rule.combination_group) return `Group: ${rule.combination_group}`
    return 'Not configured (standalone by default)'
  }

  return (
    <MobilePage>
      <div className="max-w-5xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate('/settings')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-4"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Settings
        </button>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Service Combination Rules</h1>
            <p className="text-gray-600 mt-2">
              Define which service categories can share a visit. Categories in the same group
              are combined into a single job when they fall on the same day. Categories not
              assigned to a group (or marked standalone) always get their own separate visit.
            </p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <SaveIcon className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Rules'}
          </button>
        </div>

        {message && (
          <div
            className={`mb-6 p-4 rounded-lg border ${
              message.type === 'success'
                ? 'bg-green-50 border-green-200 text-green-700'
                : 'bg-red-50 border-red-200 text-red-700'
            }`}
          >
            {message.text}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : categories.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No service categories found. Add categories in the Service Catalog first.
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service Category
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Combination Group
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Standalone
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    Clear
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {categories.map((cat) => {
                  const rule = rules[cat.id]
                  const isStandalone = rule?.is_standalone ?? false
                  const group = rule?.combination_group ?? ''
                  return (
                    <tr key={cat.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{cat.name}</td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          list="existing-groups"
                          value={isStandalone ? '' : group}
                          disabled={isStandalone}
                          onChange={(e) =>
                            updateRule(cat.id, 'combination_group', e.target.value)
                          }
                          placeholder={isStandalone ? '(standalone)' : 'e.g., DSNY Exterior'}
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:bg-gray-100 disabled:text-gray-400"
                        />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={isStandalone}
                          onChange={(e) =>
                            updateRule(cat.id, 'is_standalone', e.target.checked)
                          }
                          className="h-4 w-4 text-blue-600 rounded border-gray-300"
                        />
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {getCategoryStatus(cat.id)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {rules[cat.id] && (
                          <button
                            type="button"
                            onClick={() => clearRule(cat.id)}
                            className="text-xs text-red-600 hover:text-red-800"
                          >
                            Clear
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
          <datalist id="existing-groups">
            {existingGroups.map((g) => (
              <option key={g} value={g} />
            ))}
          </datalist>
        </div>

        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-900 mb-1">How it works</h3>
          <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
            <li>
              Categories with the <strong>same combination group</strong> AND the same recurrence
              days are merged into a single visit (one job per occurrence day).
            </li>
            <li>
              Categories marked <strong>standalone</strong> always produce a separate visit,
              regardless of days.
            </li>
            <li>
              Categories with <strong>no rule</strong> default to standalone (separate visit).
            </li>
            <li>
              These rules apply globally. You can still customize visit groups on individual
              contracts when needed.
            </li>
          </ul>
        </div>
      </div>
    </MobilePage>
  )
}
